import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  BookOutlined,
  DeleteOutlined,
  EditOutlined,
  IdcardOutlined,
  PhoneOutlined,
  PlusOutlined,
  StopOutlined,
  TrophyOutlined,
  UndoOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  hrApi,
  type Qualification,
  type QualificationInput,
  type StaffDetail,
} from '@/api/hr';
import { hrDepthApi, type StaffDocument } from '@/api/hr-depth';
import { filesApi } from '@/api/files';
import { useAuthStore } from '@/store/auth';
import { useAbility } from '@/auth/ability-provider';
import { ProfileHero } from '@/shared/components/profile-hero';
import { SummaryRail, type SummaryItem } from '@/shared/components/summary-rail';
import { MaskedAmount, RevealGroup, RevealToggle } from '@/shared/components/masked-amount';
import { extractError } from '@/shared/lib/error-message';
import { fileUrl } from '@/shared/lib/file-url';
import { usePhotoUpload } from '@/shared/lib/use-photo-upload';
import { StaffForm, valuesToInput, type StaffFormShape } from './staff-form';
import { CreateUserFromStaffModal } from './create-user-from-staff-modal';
import { Upload as AntUpload } from 'antd';
import { UploadOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';

export function HrStaffDetailPage() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['hr', 'staff', 'detail', id],
    queryFn: () => hrApi.getStaff(id),
    enabled: Number.isFinite(id),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const canCreateUser = useAuthStore((s) => s.hasAuthority('USER_MGMT:CREATE'));

  if (isLoading) {
    return (
      <div style={{ padding: 16 }}>
        <Card loading />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load staff"
        description={extractError(error)}
        action={<Button onClick={() => navigate('/hr/staff')}>Back</Button>}
      />
    );
  }

  const fullName = `${data.firstName} ${data.lastName}`;
  const initials = (data.firstName.charAt(0) + (data.lastName.charAt(0) || '')).toUpperCase();
  const tenureYears = dayjs().diff(dayjs(data.joiningDate), 'year');
  const tenureMonths = dayjs().diff(dayjs(data.joiningDate), 'month') % 12;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <ProfileHero
        eyebrow="Module / Staff"
        title={fullName}
        avatar={{ src: data.photoPath, fallback: initials, shape: 'circle', size: 88 }}
        statusPill={
          data.active
            ? { label: 'Active', tone: 'success' }
            : { label: `Left${data.leavingDate ? ` · ${dayjs(data.leavingDate).format('D MMM YYYY')}` : ''}`, tone: 'default' }
        }
        subtitle={
          <Space size={10} wrap>
            {data.designationName && <Tag variant="filled">{data.designationName}</Tag>}
            {data.departmentName && <Tag variant="filled">{data.departmentName}</Tag>}
            {data.employmentTypeName && (
              <Tag variant="filled" color="processing">
                {data.employmentTypeName}
              </Tag>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Joined {dayjs(data.joiningDate).format('D MMM YYYY')} ·{' '}
              {tenureYears > 0
                ? `${tenureYears}y ${tenureMonths}m`
                : `${tenureMonths} mo`}
            </Typography.Text>
          </Space>
        }
        actions={
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/staff')}>
              Back
            </Button>
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              Edit profile
            </Button>
            {data.linkedUsername ? (
              <Tag color={data.linkedUserActive === false ? 'default' : 'blue'} style={{ alignSelf: 'center' }}>
                Login: {data.linkedUsername}
              </Tag>
            ) : (
              canCreateUser && (
                <Button icon={<UserAddOutlined />} onClick={() => setCreateUserOpen(true)}>
                  Create user
                </Button>
              )
            )}
            <StatusButtons staff={data} />
          </Space>
        }
      />

      <CreateUserFromStaffModal
        staff={data}
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
      />

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={16}>
          <Tabs
            defaultActiveKey="profile"
            items={[
              { key: 'profile', label: 'Profile', children: <ProfileTab staff={data} /> },
              {
                key: 'qualifications',
                label: `Qualifications & experience${data.qualifications.length ? ` · ${data.qualifications.length}` : ''}`,
                children: <QualificationsTab staff={data} />,
              },
              {
                key: 'documents',
                label: 'Documents',
                children: <DocumentsTab staffId={data.id} />,
              },
              {
                key: 'evaluations',
                label: 'Evaluations',
                children: <EvaluationsTab staffId={data.id} />,
              },
            ]}
          />
        </Col>
        <Col xs={24} lg={8}>
          <StaffSummary staff={data} />
        </Col>
      </Row>

      <Drawer
        title={`Edit ${fullName}`}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size={760}
        destroyOnHidden
      >
        {editOpen && <EditDrawerForm staff={data} onDone={() => setEditOpen(false)} />}
      </Drawer>
    </div>
  );
}

/* ---------- Summary rail ---------- */

function StaffSummary({ staff }: { staff: StaffDetail }) {
  const ability = useAbility();
  const canSeeSalary = ability.can('view', 'StaffSalary');

  const structureQuery = useQuery({
    queryKey: ['hr', 'salary', 'current', staff.id],
    queryFn: () => hrDepthApi.currentStructure(staff.id),
    staleTime: 60 * 1000,
    enabled: canSeeSalary,
  });
  const advancesQuery = useQuery({
    queryKey: ['hr', 'advances', 'staff', staff.id],
    queryFn: () => hrDepthApi.staffAdvances(staff.id),
    staleTime: 60 * 1000,
    enabled: canSeeSalary,
  });

  const structure = structureQuery.data;
  const advances = advancesQuery.data ?? [];
  const outstandingAdvance = advances.reduce(
    (acc, a) => acc + Number(a.outstanding ?? 0),
    0,
  );

  const salaryItems: SummaryItem[] = canSeeSalary ? [
    {
      label: 'Gross salary',
      value: structure
        ? <MaskedAmount value={`Rs ${Number(structure.grossSalary).toLocaleString('en-PK')}`} />
        : '—',
      hint: structure?.effectiveFrom
        ? `Since ${dayjs(structure.effectiveFrom).format('D MMM YYYY')}`
        : 'No salary structure set',
    },
    {
      label: 'Outstanding advance',
      value: outstandingAdvance > 0
        ? <MaskedAmount value={`Rs ${outstandingAdvance.toLocaleString('en-PK')}`} />
        : 'None',
      hint: advances.filter((a) => !a.fullyRecovered).length > 0
        ? `${advances.filter((a) => !a.fullyRecovered).length} active`
        : 'All cleared',
      tone: outstandingAdvance > 0 ? 'warning' : 'success',
    },
  ] : [];

  const items: SummaryItem[] = [
    ...salaryItems,
    {
      label: 'Status',
      value: staff.active ? 'Active' : 'Left',
      hint: staff.active
        ? `Joined ${dayjs(staff.joiningDate).format('D MMM YYYY')}`
        : staff.leavingDate
          ? `Left ${dayjs(staff.leavingDate).format('D MMM YYYY')}`
          : undefined,
      tone: staff.active ? 'success' : 'neutral',
    },
  ];

  const rail = (
    <SummaryRail
      items={items}
      isLoading={canSeeSalary && (structureQuery.isLoading || advancesQuery.isLoading)}
      headerExtra={canSeeSalary ? <RevealToggle /> : undefined}
    />
  );
  return canSeeSalary ? <RevealGroup>{rail}</RevealGroup> : rail;
}

function StatusButtons({ staff }: { staff: StaffDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));
  const [terminating, setTerminating] = useState(false);
  const [leavingDate, setLeavingDate] = useState<Dayjs>(dayjs());
  const [reason, setReason] = useState('');

  const terminate = useMutation({
    mutationFn: () =>
      hrApi.terminateStaff(staff.id, {
        leavingDate: leavingDate.format('YYYY-MM-DD'),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      message.success('Staff marked as left');
      qc.invalidateQueries({ queryKey: ['hr'] });
      setTerminating(false);
      setReason('');
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reactivate = useMutation({
    mutationFn: () => hrApi.reactivateStaff(staff.id),
    onSuccess: () => {
      message.success('Staff reactivated');
      qc.invalidateQueries({ queryKey: ['hr'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  if (!canEdit) return null;
  return (
    <>
      {staff.active ? (
        <Button danger icon={<StopOutlined />} onClick={() => setTerminating(true)}>
          Terminate
        </Button>
      ) : (
        <Popconfirm
          title="Reactivate staff?"
          okText="Reactivate"
          onConfirm={() => reactivate.mutate()}
        >
          <Button icon={<UndoOutlined />}>Reactivate</Button>
        </Popconfirm>
      )}

      <Modal
        title={`Terminate ${staff.firstName} ${staff.lastName}`}
        open={terminating}
        onCancel={() => {
          setTerminating(false);
          setReason('');
        }}
        okText="Confirm"
        okButtonProps={{ danger: true, disabled: !reason.trim() || terminate.isPending }}
        confirmLoading={terminate.isPending}
        onOk={() => terminate.mutate()}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <label style={{ fontWeight: 500 }}>Leaving date</label>
            <DatePicker
              value={leavingDate}
              onChange={(d) => d && setLeavingDate(d)}
              format="DD MMM YYYY"
              allowClear={false}
              style={{ width: '100%', marginTop: 6 }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 500 }}>Reason (required)</label>
            <Input.TextArea
              autoFocus
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

/* ---------- Profile tab ---------- */

function ProfileTab({ staff }: { staff: StaffDetail }) {
  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {!staff.active && staff.leavingReason && (
        <Alert
          type="warning"
          showIcon
          message={`Left on ${dayjs(staff.leavingDate ?? '').format('D MMM YYYY')}`}
          description={staff.leavingReason}
        />
      )}

      <ProfileSection icon={<IdcardOutlined />} title="Identity">
        <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Full name">{staff.firstName} {staff.lastName}</Descriptions.Item>
          <Descriptions.Item label="Date of birth">
            {staff.dateOfBirth ? dayjs(staff.dateOfBirth).format('D MMM YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Gender">{staff.genderName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="CNIC">{staff.cnic ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Phone">{staff.phone ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="City">{staff.cityName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>
            {staff.addressLine ?? <Typography.Text type="secondary">—</Typography.Text>}
          </Descriptions.Item>
        </Descriptions>
      </ProfileSection>

      <ProfileSection icon={<ApartmentOutlined />} title="Employment">
        <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Designation">{staff.designationName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Department">{staff.departmentName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Employment type">{staff.employmentTypeName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Joining date">
            {dayjs(staff.joiningDate).format('D MMM YYYY')}
          </Descriptions.Item>
          {staff.leavingDate && (
            <Descriptions.Item label="Leaving date" span={2}>
              {dayjs(staff.leavingDate).format('D MMM YYYY')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </ProfileSection>

      <ProfileSection icon={<BankOutlined />} title="Bank account">
        {staff.bankName || staff.bankAccountNumber || staff.iban ? (
          <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
            <Descriptions.Item label="Bank">{staff.bankName ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Account #">{staff.bankAccountNumber ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Branch">{staff.bankBranch ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="IBAN">{staff.iban ?? '—'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Typography.Text type="secondary">No bank details on file</Typography.Text>}
          />
        )}
      </ProfileSection>

      <ProfileSection icon={<PhoneOutlined />} title="Emergency contact">
        {staff.emergencyContactName ? (
          <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
            <Descriptions.Item label="Name">{staff.emergencyContactName}</Descriptions.Item>
            <Descriptions.Item label="Phone">{staff.emergencyContactPhone ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Relation">{staff.emergencyRelation ?? '—'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Typography.Text type="secondary">No emergency contact on file</Typography.Text>}
          />
        )}
      </ProfileSection>
    </Space>
  );
}

function ProfileSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      styles={{ body: { padding: '14px 18px 16px' } }}
      title={
        <Space size={8} style={{ fontSize: 14 }}>
          <span style={{ color: 'var(--ant-color-primary)', fontSize: 16 }}>{icon}</span>
          <span>{title}</span>
        </Space>
      }
    >
      {children}
    </Card>
  );
}

/* ---------- Edit drawer form ---------- */

function EditDrawerForm({ staff, onDone }: { staff: StaffDetail; onDone: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<StaffFormShape>();
  const photo = usePhotoUpload('staff-photos');

  useEffect(() => {
    form.setFieldsValue({
      firstName: staff.firstName,
      lastName: staff.lastName,
      dateOfBirth: staff.dateOfBirth ? dayjs(staff.dateOfBirth) : undefined,
      genderId: staff.genderId ?? undefined,
      photoPath: staff.photoPath ?? null,
      cnic: staff.cnic ?? '',
      addressLine: staff.addressLine ?? '',
      cityId: staff.cityId ?? undefined,
      phone: staff.phone ?? '',
      designationId: staff.designationId ?? undefined,
      departmentId: staff.departmentId ?? undefined,
      employmentTypeId: staff.employmentTypeId,
      joiningDate: dayjs(staff.joiningDate),
      bankName: staff.bankName ?? '',
      bankAccountNumber: staff.bankAccountNumber ?? '',
      bankBranch: staff.bankBranch ?? '',
      iban: staff.iban ?? '',
      emergencyContactName: staff.emergencyContactName ?? '',
      emergencyContactPhone: staff.emergencyContactPhone ?? '',
      emergencyRelation: staff.emergencyRelation ?? '',
    });
  }, [form, staff]);

  const update = useMutation({
    mutationFn: (values: StaffFormShape) => hrApi.updateStaff(staff.id, valuesToInput(values)),
    onSuccess: () => {
      message.success('Staff updated');
      qc.invalidateQueries({ queryKey: ['hr', 'staff', 'detail', staff.id] });
      qc.invalidateQueries({ queryKey: ['hr', 'staff', 'search'] });
      onDone();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const submit = async (values: StaffFormShape) => {
    try {
      const uploaded = await photo.commit();
      if (uploaded !== undefined) values = { ...values, photoPath: uploaded };
    } catch (e) {
      message.error(extractError(e, 'Photo upload failed'));
      return;
    }
    update.mutate(values);
  };

  return (
    <div>
      <StaffForm
        form={form}
        layout="inline"
        onFinish={submit}
        onPhotoPendingChange={photo.onPendingChange}
      />
      <Space>
        <Button onClick={onDone}>Cancel</Button>
        <Button type="primary" loading={update.isPending} onClick={() => form.submit()}>
          Save
        </Button>
      </Space>
    </div>
  );
}

/* ---------- Qualifications tab ---------- */

interface QualFormShape {
  experience: boolean;
  degreeTitle: string;
  institution: string;
  yearCompleted?: number;
  grade?: string;
  orgName?: string;
  fromDate?: Dayjs;
  toDate?: Dayjs;
}

function QualificationsTab({ staff }: { staff: StaffDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));
  const [editing, setEditing] = useState<Qualification | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<QualFormShape>();

  const upsert = useMutation({
    mutationFn: (values: QualFormShape) => {
      const body: QualificationInput = {
        experience: values.experience,
        degreeTitle: values.degreeTitle.trim(),
        institution: values.institution.trim(),
        yearCompleted: values.yearCompleted ?? null,
        grade: values.grade?.trim() || undefined,
        orgName: values.orgName?.trim() || undefined,
        fromDate: values.fromDate ? values.fromDate.format('YYYY-MM-DD') : null,
        toDate: values.toDate ? values.toDate.format('YYYY-MM-DD') : null,
      };
      return editing
        ? hrApi.updateQualification(staff.id, editing.id, body)
        : hrApi.addQualification(staff.id, body);
    },
    onSuccess: () => {
      message.success(editing ? 'Updated' : 'Added');
      qc.invalidateQueries({ queryKey: ['hr', 'staff', 'detail', staff.id] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (qid: number) => hrApi.deleteQualification(staff.id, qid),
    onSuccess: () => {
      message.success('Removed');
      qc.invalidateQueries({ queryKey: ['hr', 'staff', 'detail', staff.id] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedExperience = Form.useWatch('experience', form);

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ experience: false } as QualFormShape);
  };
  const openEdit = (q: Qualification) => {
    setCreating(false);
    setEditing(q);
    form.setFieldsValue({
      experience: q.experience,
      degreeTitle: q.degreeTitle,
      institution: q.institution,
      yearCompleted: q.yearCompleted ?? undefined,
      grade: q.grade ?? '',
      orgName: q.orgName ?? '',
      fromDate: q.fromDate ? dayjs(q.fromDate) : undefined,
      toDate: q.toDate ? dayjs(q.toDate) : undefined,
    });
  };

  const open = creating || editing != null;

  const academic = staff.qualifications.filter((q) => !q.experience);
  const experience = staff.qualifications.filter((q) => q.experience);

  const academicSorted = academic
    .slice()
    .sort((a, b) => (b.yearCompleted ?? 0) - (a.yearCompleted ?? 0));
  const experienceSorted = experience.slice().sort((a, b) => {
    const aTo = a.toDate ? dayjs(a.toDate).valueOf() : Date.now();
    const bTo = b.toDate ? dayjs(b.toDate).valueOf() : Date.now();
    return bTo - aTo;
  });

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {canEdit && (
          <Button icon={<PlusOutlined />} onClick={openCreate}>
            Add entry
          </Button>
        )}
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={
              <Space size={8}>
                <BookOutlined style={{ color: 'var(--ant-color-primary)' }} />
                <span>Academic</span>
              </Space>
            }
            styles={{ body: { padding: '14px 18px 16px' } }}
          >
            {academicSorted.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Typography.Text type="secondary">No academic qualifications</Typography.Text>}
              />
            ) : (
              <Timeline
                mode="left"
                items={academicSorted.map((q) => ({
                  color: 'blue',
                  label: (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {q.yearCompleted ?? '—'}
                    </Typography.Text>
                  ),
                  children: (
                    <QualifierItem
                      title={q.degreeTitle}
                      subtitle={q.institution}
                      meta={q.grade ? `Grade: ${q.grade}` : undefined}
                      onEdit={canEdit ? () => openEdit(q) : undefined}
                      onRemove={canEdit ? () => remove.mutate(q.id) : undefined}
                    />
                  ),
                }))}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <Space size={8}>
                <TrophyOutlined style={{ color: 'var(--ant-color-primary)' }} />
                <span>Experience</span>
              </Space>
            }
            styles={{ body: { padding: '14px 18px 16px' } }}
          >
            {experienceSorted.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Typography.Text type="secondary">No prior experience</Typography.Text>}
              />
            ) : (
              <Timeline
                mode="left"
                items={experienceSorted.map((q) => {
                  const fromLabel = q.fromDate ? dayjs(q.fromDate).format('MMM YY') : '—';
                  const toLabel = q.toDate ? dayjs(q.toDate).format('MMM YY') : 'now';
                  return {
                    color: q.toDate ? 'gray' : 'green',
                    label: (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {fromLabel} – {toLabel}
                      </Typography.Text>
                    ),
                    children: (
                      <QualifierItem
                        title={q.degreeTitle}
                        subtitle={q.orgName ?? '—'}
                        onEdit={canEdit ? () => openEdit(q) : undefined}
                        onRemove={canEdit ? () => remove.mutate(q.id) : undefined}
                      />
                    ),
                  };
                })}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title={editing ? 'Edit entry' : 'Add academic / experience entry'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
        width={620}
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => upsert.mutate(values)}
          >
            <Form.Item label="Type" name="experience" valuePropName="checked" tooltip="Off = academic qualification, on = prior experience">
              <Switch checkedChildren="Experience" unCheckedChildren="Academic" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={watchedExperience ? 24 : 16}>
                <Form.Item
                  label={watchedExperience ? 'Role / title' : 'Degree / certificate'}
                  name="degreeTitle"
                  rules={[{ required: true, max: 200 }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              {!watchedExperience && (
                <Col span={8}>
                  <Form.Item label="Year" name="yearCompleted">
                    <InputNumber style={{ width: '100%' }} min={1900} max={2100} />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12}>
                <Form.Item label="Institution" name="institution" rules={[{ required: true, max: 200 }]}>
                  <Input />
                </Form.Item>
              </Col>
              {!watchedExperience && (
                <Col xs={24} sm={12}>
                  <Form.Item label="Grade" name="grade" rules={[{ max: 20 }]}>
                    <Input />
                  </Form.Item>
                </Col>
              )}
              {watchedExperience && (
                <>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Organization" name="orgName" rules={[{ max: 200 }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="From" name="fromDate">
                      <DatePicker style={{ width: '100%' }} format="MMM YYYY" picker="month" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="To (leave empty for present)" name="toDate">
                      <DatePicker style={{ width: '100%' }} format="MMM YYYY" picker="month" />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        )}
      </Modal>
    </Space>
  );
}

function QualifierItem({
  title,
  subtitle,
  meta,
  onEdit,
  onRemove,
}: {
  title: string;
  subtitle: string;
  meta?: string;
  onEdit?: () => void;
  onRemove?: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <Typography.Text strong>{title}</Typography.Text>
        <div>
          <Typography.Text style={{ fontSize: 13 }}>{subtitle}</Typography.Text>
        </div>
        {meta && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {meta}
          </Typography.Text>
        )}
      </div>
      {(onEdit || onRemove) && (
        <Space size={2}>
          {onEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
          )}
          {onRemove && (
            <Popconfirm title="Remove?" onConfirm={onRemove} okButtonProps={{ danger: true }}>
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )}
    </div>
  );
}

/* ---------- Documents tab ---------- */

function DocumentsTab({ staffId }: { staffId: number }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const docs = useQuery({
    queryKey: ['hr', 'staff', staffId, 'documents'],
    queryFn: () => hrDepthApi.listStaffDocuments(staffId),
  });

  const [uploadingName, setUploadingName] = useState('');

  const add = useMutation({
    mutationFn: async (file: File) => {
      const name = uploadingName.trim() || file.name;
      const result = await filesApi.upload(file, 'staff-documents');
      return hrDepthApi.addStaffDocument(staffId, { documentName: name, filePath: result.path });
    },
    onSuccess: () => {
      message.success('Document attached');
      qc.invalidateQueries({ queryKey: ['hr', 'staff', staffId, 'documents'] });
      setUploadingName('');
    },
    onError: (e) => message.error(extractError(e, 'Upload failed')),
  });

  const remove = useMutation({
    mutationFn: (docId: number) => hrDepthApi.deleteStaffDocument(staffId, docId),
    onSuccess: () => {
      message.success('Document removed');
      qc.invalidateQueries({ queryKey: ['hr', 'staff', staffId, 'documents'] });
    },
    onError: (e) => message.error(extractError(e, 'Delete failed')),
  });

  const list = docs.data ?? [];

  return (
    <Card
      title="Attached documents"
      extra={
        canEdit && (
          <Space>
            <Input
              placeholder="Document name (optional — defaults to file name)"
              value={uploadingName}
              onChange={(e) => setUploadingName(e.target.value)}
              maxLength={200}
              style={{ width: 280 }}
            />
            <AntUpload
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              showUploadList={false}
              beforeUpload={(file) => {
                add.mutate(file);
                return false;  // prevent default upload — we handle it ourselves via filesApi
              }}
              disabled={add.isPending}
            >
              <Button icon={<UploadOutlined />} loading={add.isPending}>
                Upload
              </Button>
            </AntUpload>
          </Space>
        )
      }
    >
      {docs.isLoading ? null : list.length === 0 ? (
        <Empty description="No documents attached yet — CNIC copy, degrees, appointment letter, etc." />
      ) : (
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          {list.map((d: StaffDocument) => (
            <Card key={d.id} size="small">
              <Row align="middle" gutter={12}>
                <Col flex="0 0 auto">
                  <FileTextOutlined style={{ fontSize: 22, color: '#888' }} />
                </Col>
                <Col flex="auto">
                  <Typography.Text strong>{d.documentName}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Uploaded {dayjs(d.uploadedAt).format('D MMM YYYY')}
                    </Typography.Text>
                  </div>
                </Col>
                <Col flex="0 0 auto">
                  <Space>
                    <Button
                      icon={<DownloadOutlined />}
                      href={fileUrl(d.filePath)}
                      target="_blank"
                      rel="noopener"
                      type="link"
                    >
                      Open
                    </Button>
                    {canEdit && (
                      <Popconfirm
                        title="Remove this document?"
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => remove.mutate(d.id)}
                      >
                        <Button danger type="text" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      )}
    </Card>
  );
}

/* ---------- Evaluations tab ---------- */

function EvaluationsTab({ staffId }: { staffId: number }) {
  const evals = useQuery({
    queryKey: ['hr', 'evaluations', 'staff', staffId],
    queryFn: () => hrDepthApi.listEvalsForStaff(staffId),
  });

  const list = evals.data ?? [];

  return (
    <Card
      title="Performance evaluations"
      extra={
        <Button type="link" href="/hr/evaluations">
          Manage on the evaluations page
        </Button>
      }
    >
      {evals.isLoading ? null : list.length === 0 ? (
        <Empty description="No evaluations recorded yet." />
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {list.map((e) => (
            <Card key={e.id} size="small">
              <Row gutter={12} align="middle">
                <Col flex="auto">
                  <Space wrap>
                    <Typography.Text strong>{e.academicYearName ?? `Year ${e.academicYearId}`}</Typography.Text>
                    {e.evaluationPeriod && <Tag>{e.evaluationPeriod}</Tag>}
                    {e.overallScore !== null && <Tag color="blue">{Number(e.overallScore).toFixed(2)} / 5</Tag>}
                    {e.overallLabel && <Tag color="green">{e.overallLabel}</Tag>}
                    {e.sharedWithStaff && <Tag color="purple">Shared</Tag>}
                  </Space>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      by {e.evaluatedByUsername ?? '—'} · {dayjs(e.updatedAt).format('D MMM YYYY')}
                    </Typography.Text>
                  </div>
                  {e.remarks && (
                    <div style={{ marginTop: 6 }}>
                      <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {e.remarks}
                      </Typography.Paragraph>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      )}
    </Card>
  );
}
